import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { useDeviceInfo } from "@swa-ui/browser";
import { Button, Caption, Dialog, Icon, Link, useCaption, useDialog } from "@swa-ui/core";
import { useTravelerSelectionModalAnalytics } from "@swa-ui/dotcom-analytics";
import { SelectFormField } from "@swa-ui/form";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import { childAgeList } from "../../defines/childAgeList";
import { FLIGHT_HOTEL, FLIGHT_HOTEL_CAR } from "../../defines/packageType";
import {
  ADULTS,
  CHILDREN,
  DEFAULT_ADULTS,
  DEFAULT_CHILD_AGE,
  LAP_CHILDREN,
  MAXIMUM_ADULTS_PER_ROOM,
  MAXIMUM_TRAVELERS_PER_ROOM,
  MINIMUM_ADULTS,
  ROOM_MAX_COUNT,
  ROOM_MIN_COUNT,
  TRAVELERS_MAX_COUNT,
} from "../../defines/travelerSelector";
import { packageTypeProps } from "../../proptypes/packageType";
import { DropdownTriggerFormField } from "./DropdownTriggerFormField";
import styles from "./TravelerSelector.module.scss";
import { TravelerSelectorInputField } from "./TravelerSelectorInputField";

export const TravelerSelector = (props) => {
  const { className, onChange, packageType, travelers, dropdown } = props;
  const { screenSize } = useDeviceInfo();
  const { captionRef, captionLocation, hideCaption, showCaption, isCaptionVisible } =
    useCaption("below");
  const { dialogRef, hideDialog, isDialogVisible, showDialog } = useDialog("center");
  const [updateFocus, setUpdateFocus] = useState(false);
  const addRoomLink = useRef();
  const removeRoomLink = useRef();
  const urlData = getBootstrapData("urls");

  useTravelerSelectionModalAnalytics(isCaptionVisible);

  useEffect(() => {
    if (updateFocus) {
      moveFocusToAddRemoveRoomButton();
      setUpdateFocus(false);
    }
  }, [updateFocus]);

  return isDialog() ? renderDialog() : renderCaption();

  function renderDialog() {
    return (
      <div className={className}>
        <Dialog {...getDialogProps()}>{renderTravelerSelectorContent()}</Dialog>
        {renderTriggerButton()}
      </div>
    );
  }

  function renderCaption() {
    return <Caption {...getCaptionProps()}>{renderTriggerButton()}</Caption>;
  }

  function renderTriggerButton() {
    return dropdown ? (
      renderDropDownUI()
    ) : (
      <Button {...getButtonProps()}>
        {renderTriggerText()}
        <Icon {...getIconProps()} />
      </Button>
    );
  }

  function renderDropDownUI() {
    return <DropdownTriggerFormField {...getDropdownTriggerProps()} />;
  }

  function renderTriggerText() {
    return `${renderRooms()}${hasRooms() ? ", " : ""}${renderTravelers()}`;
  }

  function renderRooms() {
    const rooms = travelers.length;

    return hasRooms() ? i18n("TravelerSelector__ROOMS_COUNT", rooms) : "";
  }

  function renderTravelers() {
    return i18n("TravelerSelector__TRAVELERS_COUNT", getAllTravelersCount());
  }

  function renderTravelerSelectorContent() {
    return (
      <div className={styles.content} ref={captionRef}>
        <div className={getRoomContainerClassNames()}>{travelers.map(renderRoomsCaption)}</div>
        <div className={styles.submitSection}>
          <Button {...getApplyButtonProps()}>{i18n("TravelerSelector__APPLY")}</Button>
        </div>
      </div>
    );
  }

  function renderRoomsCaption(room, index) {
    return (
      <div key={`room-${index}`} className={styles.roomsCaptionContainer}>
        {renderRoomHeader(index)}
        <div className={styles.inputFieldContainer}>
          <TravelerSelectorInputField {...getInputFieldProps(ADULTS, room.adults, index)} />
        </div>
        <div className={styles.inputFieldContainer}>
          <TravelerSelectorInputField {...getInputFieldProps(CHILDREN, room.children, index)} />
          {renderChildrenAgeSelector(room, index)}
        </div>
        <div className={styles.inputFieldContainer}>
          <TravelerSelectorInputField
            {...getInputFieldProps(LAP_CHILDREN, room.lapChildren, index)}
          />
        </div>
        {index === 0 && renderBabyOnBoardMessage()}
      </div>
    );
  }

  function renderChildrenAgeSelector(room, roomIndex) {
    return (
      room.childrenAge.length > 0 && (
        <>
          <div className={styles.childrenAgeHeading}>{i18n("TravelerSelector__SELECT_AGE")}</div>
          <div className={styles.childrenAgeContainer}>
            {room.childrenAge.map((childAge, childIndex) => {
              const formattedChildAge = childAge === 0 ? "<1" : childAge;

              return (
                <div key={`room-${roomIndex}-child-${childIndex}`} className={styles.childAgeField}>
                  <SelectFormField
                    {...getChildAgeSelectorProps(formattedChildAge, roomIndex, childIndex)}
                  />
                </div>
              );
            })}
          </div>
        </>
      )
    );
  }

  function renderBabyOnBoardMessage() {
    return (
      <div className={styles.babyOnBoardContainer}>
        <div className={styles.babyOnBoardHeading}>
          {i18n("TravelerSelector__BABY_ON_BOARD_TITLE")}
        </div>
        <div className={styles.babyOnBoardDescription}>
          {i18n("TravelerSelector__BABY_ON_BOARD_DESCRIPTION")}
          <Link {...getLearnMoreLinkProps()}>
            {i18n("TravelerSelector__BABY_ON_BOARD_LEARN_MORE")}
          </Link>
        </div>
      </div>
    );
  }

  function renderRoomHeader(index) {
    const roomNumber = index + 1;
    const addRoomEnabled =
      travelers.length < ROOM_MAX_COUNT &&
      roomNumber === travelers.length &&
      getAllTravelersCount() < TRAVELERS_MAX_COUNT;
    const removeRoomEnabled = travelers.length > ROOM_MIN_COUNT && roomNumber === travelers.length;

    return (
      hasRooms() && (
        <div className={styles.roomSelectionContainer}>
          <div className={styles.roomSelectionHeading}>
            {i18n("TravelerSelector__ROOM_NUMBER", roomNumber)}
          </div>
          {removeRoomEnabled && (
            <Link {...getRemoveRoomProps(roomNumber)}>{i18n("TravelerSelector__REMOVE_ROOM")}</Link>
          )}
          {addRoomEnabled && (
            <Link {...getAddRoomProps(roomNumber + 1)}>{i18n("TravelerSelector__ADD_ROOM")}</Link>
          )}
        </div>
      )
    );
  }

  function getDialogProps() {
    return {
      buttonGroupProps: {
        "aria-label-next": i18n("TravelerSelector__BUTTON_GROUP_ARIA_NEXT"),
        "aria-label-previous": i18n("TravelerSelector__BUTTON_GROUP_ARIA_PREVIOUS"),
      },
      className,
      contentClassName: styles.dialogContent,
      dialogRef,
      fullScreen: true,
      fullWidth: true,
      onClose: hideDialog,
      portal: false,
      revealed: isDialogVisible,
      showClose: true,
    };
  }

  function getDropdownTriggerProps() {
    return {
      componentProps: {
        lisCaptionVisible: isDialog() ? isDialogVisible : isCaptionVisible,
        onTrigger: isDialog() ? showDialog : showCaption,
        text: renderTriggerText(),
      },
      label: `${i18n("TravelerSelector__TRAVELER_SELECTOR_LABEL")}*`,
      name: "travelersDropdownUI",
    };
  }

  function getButtonProps() {
    return {
      className: styles.buttonTrigger,
      onClick: isDialog() ? showDialog : showCaption,
      styleType: "no-style",
    };
  }

  function getIconProps() {
    return {
      className: styles.buttonTriggerIcon,
      color: "primary-500",
      name: "ArrowSolid",
      role: "presentation",
      size: "30",
    };
  }

  function getInputFieldProps(passengerType, value, roomIndex) {
    const travelersInRoom = travelers[roomIndex];

    return {
      "aria-label": i18n(
        `TravelerSelector__ARIA_LABEL__${passengerType.toUpperCase()}_INPUT`,
        value
      ),
      defaultValue: value,
      maximumValue: getMaximumValue(travelersInRoom, passengerType),
      minimumValue: passengerType === ADULTS ? 1 : 0,
      onChange: (newValue) => handleInputFieldChange(passengerType, roomIndex, newValue),
      passengerType,
      roomIndex,
    };
  }

  function getMaximumValue(travelersInRoom, passengerType) {
    const maximumPassengersPerRoom = {
      [ADULTS]: getMaximumAdultsInRoom(travelersInRoom),
      [CHILDREN]: getMaximumChildrenInRoom(travelersInRoom),
      [LAP_CHILDREN]: getMaximumLapChildrenInRoom(travelersInRoom),
    };
    let maximumValue =
      hasRooms() && getAllTravelersCount() === TRAVELERS_MAX_COUNT
        ? travelersInRoom[passengerType]
        : maximumPassengersPerRoom[passengerType];

    return maximumValue;
  }

  function getMaximumAdultsInRoom(travelersInRoom) {
    const emptyTravelerSlots = hasRooms()
      ? MAXIMUM_TRAVELERS_PER_ROOM - travelersInRoom[CHILDREN] - travelersInRoom[LAP_CHILDREN]
      : TRAVELERS_MAX_COUNT - travelersInRoom[CHILDREN];
    let maximumAdults = hasRooms() ? MAXIMUM_ADULTS_PER_ROOM : TRAVELERS_MAX_COUNT;

    if (emptyTravelerSlots < maximumAdults) {
      maximumAdults = emptyTravelerSlots;
    }

    return maximumAdults;
  }

  function getMaximumChildrenInRoom(travelersInRoom) {
    return hasRooms()
      ? MAXIMUM_TRAVELERS_PER_ROOM - travelersInRoom[ADULTS] - travelersInRoom[LAP_CHILDREN]
      : TRAVELERS_MAX_COUNT - travelersInRoom[ADULTS];
  }

  function getMaximumLapChildrenInRoom(travelersInRoom) {
    const numberOfOpenSlotsInRoom =
      MAXIMUM_TRAVELERS_PER_ROOM - travelersInRoom[ADULTS] - travelersInRoom[CHILDREN];
    let maxLapChildrenInRoom = travelersInRoom[ADULTS];

    if (travelersInRoom[ADULTS] > numberOfOpenSlotsInRoom) {
      maxLapChildrenInRoom = numberOfOpenSlotsInRoom;
    }

    return hasRooms() ? maxLapChildrenInRoom : travelersInRoom[ADULTS];
  }

  function getCaptionProps() {
    return {
      adjoiningContent: renderTravelerSelectorContent(),
      alignment: "center",
      bestFit: false,
      className,
      location: captionLocation,
      pointerAlignment: "center",
      showPointer: true,
    };
  }

  function getChildAgeSelectorProps(childAge, roomIndex, childIndex) {
    return {
      componentProps: {
        "aria-label": i18n("TravelerSelector__ARIA_LABEL__CHILD_AGE_SELECTION", childIndex + 1),
        bestFit: true,
        className: styles.childrenAgeInput,
        defaultValue: childAge.toString(),
        list: getChildAgeList(),
        maxItemsToDisplay: screenSize === "small" ? 5 : 8,
        onChange: (event) => handleChildAgeInputChange(event.target.value, roomIndex, childIndex),
        size: "small",
        value: childAge.toString(),
      },
      hideBottomSection: true,
      label: i18n("TravelerSelector__CHILD_NUMBER", childIndex + 1),
      name: `childAge${childIndex}Room${roomIndex}`,
    };
  }

  function getChildAgeList() {
    return childAgeList.map((childAge) => ({
      label: <div className={styles.childAgeListItem}>{childAge}</div>,
      value: childAge,
    }));
  }

  function getAddRoomProps(roomNumber) {
    return {
      "aria-label": i18n("TravelerSelector__ARIA_LABEL__ADD_ROOM", roomNumber),
      className: styles.addRoom,
      emphasis: true,
      id: "travelerSelectorAddRoom",
      onClick: handleAddRoom,
      ref: addRoomLink,
      size: "fontSize12",
    };
  }

  function getRemoveRoomProps(roomNumber) {
    return {
      "aria-label": i18n("TravelerSelector__ARIA_LABEL__REMOVE_ROOM", roomNumber),
      className: styles.removeRoom,
      emphasis: true,
      id: "travelerSelectorRemoveRoom",
      onClick: handleRemoveRoom,
      ref: removeRoomLink,
    };
  }

  function getLearnMoreLinkProps() {
    return {
      "aria-label": i18n("TravelerSelector__ARIA_LABEL__LEARN_MORE"),
      emphasis: false,
      href: urlData.LAP_CHILD_POLICY,
      newWindow: true,
      size: "fontSize14",
      styleType: "link",
    };
  }

  function getApplyButtonProps() {
    return {
      className: styles.travelerSelectorApplyButton,
      id: "travelerSelectorApply",
      onClick: isDialog() ? hideDialog : hideCaption,
      styleType: "primary",
    };
  }

  function getRoomContainerClassNames() {
    return classNames({
      [styles.selectorContainer]: true,
      [styles.selectorContainerRowGap]: travelers.length > 1,
    });
  }

  function handleAddRoom() {
    const newTravelers = [...travelers];

    newTravelers.push({
      adults:
        getAllTravelersCount() === TRAVELERS_MAX_COUNT - MINIMUM_ADULTS
          ? MINIMUM_ADULTS
          : DEFAULT_ADULTS,
      children: 0,
      childrenAge: [],
      lapChildren: 0,
    });
    onChange(newTravelers);
    setUpdateFocus(true);
  }

  function handleRemoveRoom() {
    const newTravelers = [...travelers];

    newTravelers.pop();
    onChange(newTravelers);
    setUpdateFocus(true);
  }

  function handleInputFieldChange(passengerType, roomIndex, newValue) {
    const newTravelers = [...travelers];
    const previousValue = travelers[roomIndex][passengerType];

    newTravelers[roomIndex][passengerType] = newValue;

    if (passengerType === ADULTS) {
      const { adults, lapChildren } = newTravelers[roomIndex];

      if (lapChildren > adults) {
        newTravelers[roomIndex].lapChildren = adults;
      }
    }

    if (passengerType === CHILDREN) {
      const childrenAgeArray = newTravelers[roomIndex].childrenAge;

      if (newTravelers[roomIndex].children > previousValue) {
        childrenAgeArray.push(DEFAULT_CHILD_AGE);
      } else {
        childrenAgeArray.pop();
      }
    }

    onChange(newTravelers);
  }

  function handleChildAgeInputChange(newValue, roomIndex, childIndex) {
    const newTravelers = [...travelers];
    const childAgeValue = Number(newValue);

    newTravelers[roomIndex].childrenAge[childIndex] = Number.isNaN(childAgeValue)
      ? 0
      : childAgeValue;
    onChange(newTravelers);
  }

  function hasRooms() {
    return packageType === FLIGHT_HOTEL || packageType === FLIGHT_HOTEL_CAR;
  }

  function getAllTravelersCount() {
    return travelers.reduce(
      (total, room) => total + room.adults + room.children + room.lapChildren,
      0
    );
  }

  function moveFocusToAddRemoveRoomButton() {
    if (addRoomLink.current) {
      addRoomLink.current?.focus();
    } else {
      removeRoomLink.current?.focus();
    }
  }

  function isDialog() {
    return screenSize === "small";
  }
};

TravelerSelector.propTypes = {
  /** Additional class to position the component.  */
  className: PropTypes.string,

  /** Determines whether text or a dropdown is used. */
  dropdown: PropTypes.string,

  /** Callback function to be called after one of the package type button groups is selected. */
  onChange: PropTypes.func.isRequired,

  /** Package type selected. */
  packageType: packageTypeProps.isRequired,

  /** List of rooms with passengers. */
  travelers: PropTypes.arrayOf(PropTypes.object).isRequired,
};
